import { useCallback, useMemo, useState } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatBytes } from '../../../../lib/formatBytes';
import { usePermission } from '../../../../auth/AuthContext';
import { DocumentVersionModal } from './DocumentVersionModal';

import type {
  RepositoryDocumentVersionListItem,
  RepositoryItemListItem,
} from '../repositoryTypes';

/**
 * A controlled document's version history — every file it has superseded.
 *
 * **A HISTORY renders as a table**, which is the 201-file rule reaching this module: it is read
 * by scanning down a column, and a stack of label/value cards makes that a scroll.
 *
 * **The rows arrive WHOLE**, so the search, the sort and the pager are in memory. That is the
 * same exemption a profile section has: the rule against client-side filtering is about a
 * *paged* list whose filter silently applies to the rows on screen and not the hundreds behind
 * them, and a document's history is a handful of rows fetched entire.
 */
export function DocumentVersions({
  document,
  versions,
}: {
  document: RepositoryItemListItem;
  /**
   * The history, already fetched by the page.
   *
   * **Taken as a prop rather than fetched here**, because the page needs the count for the
   * tab's badge and a second fetch for the same list is the same question asked twice. It is
   * also the shape CLAUDE.md asks a new section panel to take — rows it can receive late —
   * so the day this tab loads lazily, nothing in here changes.
   */
  versions: RepositoryDocumentVersionListItem[];
}) {
  /*
    The rows are already in hand, so this hands them straight back as one page. `useDataTable`
    is still what owns the search, the sort and the pager — the alternative is a second table
    component for short lists, which is the drift the two shells exist to prevent.
  */
  const fetchPage = useCallback(
    async () => ({ items: versions, total: versions.length }),
    [versions],
  );

  const state = useDataTable<RepositoryDocumentVersionListItem>({
    fetchPage,
    pageSize: 25,
  });

  /**
   * The version whose file is open, or null.
   *
   * A superseded file is RETAINED — replacing one archives it and points the document at the
   * new bytes, destroying nothing — so every row here has a real file behind it. Until this
   * existed the column stated a filename nobody could open.
   */
  const [opened, setOpened] = useState<RepositoryDocumentVersionListItem | null>(null);

  /*
    The FILE cell is the click target, so the columns close over the opener and cannot be a
    module-level constant any more. Memoised on it: `useDataTable` and `<DataTable>` key column
    widths off `columns`, and a fresh array every render would re-seed them.
  */
  const columns = useMemo(() => buildColumns(setOpened), []);

  return (
    <div className="flex h-full min-w-0 flex-col">
      <DataTablePanel
        id="repository-document-versions"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search this history"
        fill
        /*
          Nothing is created, edited or deleted here — a version is written by revising the
          document, and a history nobody can rewrite is the point of keeping one. So no
          `onCreate`, no row actions, and no checkbox: `selectable={false}` is for a table
          nothing can ever be done to, which is exactly this one.
        */
        selectable={false}
        /*
          No `otherInfo`, so no Table/List switch and no audit trio — the columns below ARE the
          provenance. Who superseded a version and when is the history, not metadata about it.
        */
        auditColumns={false}
        emptyMessage={
          document.storedFileId === null
            ? 'This document has no file, so it has no history.'
            : 'No earlier versions. This document is still on its first issue.'
        }
        emptySearchMessage="No version matches that search."
      />

      {opened ? (
        <DocumentVersionModal
          open
          document={document}
          version={opened}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The file cell — a button that opens the archived version.
 *
 * **`font-medium text-slate-800 hover:underline`, not the anchor blue.** The blue is carved out
 * for a table's ROW ANCHOR and is scoped by `AnchorCellContext`, which only the shells provide
 * for column 0; this is a clickable thing that is not an anchor, so it takes the product's
 * ordinary black treatment. A class copied off an anchor here is exactly the spread that
 * context exists to prevent.
 *
 * **Gated on `repository.download`, and it degrades to plain text.** What it opens previews and
 * hands over the bytes, which is that key's whole subject — so a reader without it still sees
 * WHICH file the version carried, and simply cannot open it. Hidden affordance, stated fact.
 */
function FileCell({
  version,
  onOpen,
}: {
  version: RepositoryDocumentVersionListItem;
  onOpen: (version: RepositoryDocumentVersionListItem) => void;
}) {
  const canDownload = usePermission('repository.download');

  if (!version.fileOriginalName) {
    return <TextCell value={null} />;
  }

  if (!canDownload) {
    return <TextCell value={version.fileOriginalName} />;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(version)}
      className="block max-w-full truncate text-left font-medium text-slate-800 hover:underline"
      title={version.fileOriginalName}
    >
      {version.fileOriginalName}
    </button>
  );
}

function buildColumns(
  onOpen: (version: RepositoryDocumentVersionListItem) => void,
): DataColumn<RepositoryDocumentVersionListItem>[] {
  return [
  {
    key: 'versionNumber',
    header: 'Version',
    width: 110,
    /*
      The ANCHOR is what identifies the row, and a version IS its number. Mono like every other
      machine value, and NOT `numeric` — it is an identifier rather than a quantity, so a zero
      would never want dimming.
    */
    render: (row) => <TextCell value={`v${row.versionNumber}`} mono />,
  },
  {
    key: 'createdAt',
    header: 'Superseded',
    width: 190,
    summary: true,
    // When this file stopped being the current one — which is when the row was written.
    render: (row) => <DateCell value={row.createdAt} />,
  },
  {
    key: 'createdByName',
    header: 'Superseded by',
    width: 190,
    summary: true,
    render: (row) => <TextCell value={row.createdByName} size="xs" />,
  },
  {
    key: 'changeDescription',
    header: 'What changed',
    width: 360,
    summary: true,
    /*
      The widest column, because it is the one value here that is prose and the one a reader is
      actually reading. A width is REQUIRED on `DataColumn` — `<DataTable>` has no width-less
      soft-flex column, unlike the index shell — so this is a number rather than an omission.
    */
    render: (row) => <TextCell value={row.changeDescription} />,
  },
  {
    key: 'fileOriginalName',
    header: 'File',
    width: 260,
    // The CLICK TARGET of the row — it opens the archived version, previewed and downloadable.
    render: (row) => <FileCell version={row} onOpen={onOpen} />,
  },
  {
    key: 'fileSizeBytes',
    header: 'Size',
    width: 110,
    align: 'right',
    render: (row) => (
      <TextCell
        value={row.fileSizeBytes === null ? null : formatBytes(row.fileSizeBytes)}
        mono
        numeric
      />
    ),
  },
  ];
}
